import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Wrench, Calendar, Wallet, ListChecks, Users } from "lucide-react";

const JOB_STATUS = [
  { value: "new", label: "Uusi" },
  { value: "in_progress", label: "Käynnissä" },
  { value: "waiting_parts", label: "Odottaa osia" },
  { value: "ready", label: "Valmis" },
  { value: "delivered", label: "Luovutettu" },
  { value: "cancelled", label: "Peruutettu" },
];
const JOB_TYPES = [
  { value: "service", label: "Huolto" },
  { value: "repair", label: "Korjaus" },
  { value: "inspection", label: "Katsastus" },
  { value: "tire_change", label: "Renkaanvaihto" },
  { value: "oil_change", label: "Öljynvaihto" },
];
const PRIORITY = [
  { value: "urgent", label: "Kiireellinen" },
  { value: "normal", label: "Normaali" },
  { value: "low", label: "Matala" },
];
const APPT_STATUS = [
  { value: "scheduled", label: "Varattu" },
  { value: "confirmed", label: "Vahvistettu" },
  { value: "in_progress", label: "Käynnissä" },
  { value: "completed", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
  { value: "no_show", label: "Ei tullut" },
];

export default function AutoRepairDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/auto-repair/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const jobs = {
    endpoint: "/auto-repair/jobs",
    testidPrefix: "jobs",
    emptyMessage: "Ei korjaustöitä",
    defaults: { customer_name: "", job_type: "service", priority: "normal", status: "new", estimated_hours: 0, work_items: [] },
    fields: [
      { key: "customer_name", label: "Asiakas", type: "text", required: true },
      { key: "customer_phone", label: "Puhelin", type: "text" },
      { key: "customer_email", label: "Sähköposti", type: "text" },
      { key: "vehicle_license_plate", label: "Rekisterinumero", type: "text" },
      { key: "vehicle_make", label: "Merkki", type: "text" },
      { key: "vehicle_model", label: "Malli", type: "text" },
      { key: "vehicle_year", label: "Vuosi", type: "number", step: "1" },
      { key: "vehicle_mileage", label: "Mittarilukema (km)", type: "number", step: "1" },
      { key: "vehicle_vin", label: "Valmistenumero (VIN)", type: "text" },
      { key: "job_type", label: "Työn tyyppi", type: "select", options: JOB_TYPES },
      { key: "priority", label: "Prioriteetti", type: "select", options: PRIORITY },
      { key: "status", label: "Tila", type: "select", options: JOB_STATUS },
      { key: "appointment_date", label: "Aika pvm", type: "date" },
      { key: "appointment_time", label: "Aika klo", type: "text", placeholder: "10:00" },
      { key: "promised_date", label: "Luvattu valmiiksi", type: "date" },
      { key: "assigned_mechanic", label: "Mekaanikko", type: "text" },
      { key: "estimated_hours", label: "Arvio tunteja", type: "number" },
      { key: "actual_hours", label: "Toteutuneet tunnit", type: "number" },
      { key: "description", label: "Työn kuvaus", type: "textarea", span: 2 },
      { key: "obd_codes", label: "OBD-koodit", type: "text", span: 2 },
      { key: "diagnosis", label: "Diagnoosi", type: "textarea", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "job_number", label: "Nro" },
      { key: "customer_name", label: "Asiakas" },
      { key: "vehicle_license_plate", label: "Rek.nro" },
      { key: "job_type", label: "Tyyppi", format: "badge" },
      { key: "appointment_date", label: "Aika" },
      { key: "total_amount", label: "Hinta", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
    getRowLabel: (j) => `#${j.job_number} ${j.customer_name}`,
  };

  const appts = {
    endpoint: "/auto-repair/appointments",
    testidPrefix: "appts",
    emptyMessage: "Ei ajanvarauksia",
    defaults: { customer_name: "", appointment_date: new Date().toISOString().slice(0, 10), appointment_time: "09:00", duration_minutes: 60, status: "scheduled" },
    fields: [
      { key: "customer_name", label: "Asiakas", type: "text", required: true },
      { key: "customer_phone", label: "Puhelin", type: "text" },
      { key: "customer_email", label: "Sähköposti", type: "text" },
      { key: "vehicle_license_plate", label: "Rekisterinumero", type: "text" },
      { key: "vehicle_make", label: "Merkki", type: "text" },
      { key: "vehicle_model", label: "Malli", type: "text" },
      { key: "service_type", label: "Palvelutyyppi", type: "text" },
      { key: "appointment_date", label: "Pvm", type: "date", required: true },
      { key: "appointment_time", label: "Klo", type: "text", required: true },
      { key: "duration_minutes", label: "Kesto (min)", type: "number", step: "15" },
      { key: "assigned_mechanic", label: "Mekaanikko", type: "text" },
      { key: "status", label: "Tila", type: "select", options: APPT_STATUS },
      { key: "description", label: "Kuvaus", type: "textarea", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "appointment_date", label: "Pvm" },
      { key: "appointment_time", label: "Klo" },
      { key: "customer_name", label: "Asiakas" },
      { key: "vehicle_license_plate", label: "Rek.nro" },
      { key: "service_type", label: "Palvelu" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="auto-repair-dashboard">
      <PageHeader kicker="Sovellus" title="Autokorjaamo" description="Korjaustyöt ja ajanvaraukset." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<ListChecks className="w-5 h-5" />} label="Aktiiviset työt" value={stats?.jobs_active ?? "—"} accent="text-amber-300" loading={loading} testid="ar-kpi-active" />
        <KpiCard icon={<Wrench className="w-5 h-5" />} label="Työt yht." value={stats?.jobs_total ?? "—"} loading={loading} testid="ar-kpi-total" />
        <KpiCard icon={<Calendar className="w-5 h-5" />} label="Varaukset tänään" value={stats?.appointments_today ?? 0} accent="text-blue-300" loading={loading} testid="ar-kpi-today" />
        <KpiCard icon={<Wallet className="w-5 h-5" />} label="Luovutettu LV" value={formatEur(stats?.revenue_delivered)} accent="text-emerald-300" loading={loading} testid="ar-kpi-revenue" />
      </div>

      <Panel title="Hallinta">
        <Tabs defaultValue="jobs" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="jobs" data-testid="ar-tab-jobs">Korjaustyöt</TabsTrigger>
            <TabsTrigger value="appts" data-testid="ar-tab-appts">Ajanvaraukset</TabsTrigger>
            <TabsTrigger value="staff" data-testid="ar-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="jobs"><CrudList config={jobs} /></TabsContent>
          <TabsContent value="appts"><CrudList config={appts} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="auto_repair" moduleLabel="Autokorjaamo" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
